import React, { useState } from 'react';
import axios from 'axios';
import { ApiType } from '../types/ApiType';
import { ConfigClientType } from '../types/ConfigClientType';
import { IconLoadSpinner } from '../common/svgicons/LoadSpinner';
import { getTranslation } from '../../constants/languages';
import { isSwedish, isNorwegian, isDanish } from '../../constants/languages';
import { isPhoneNumberValid, PhoneCountryCode, sanitizePhoneNumber } from '../../util/phoneNumber';
import { isOutsideOpeningHours } from '../../util/time';

type WcbButtonOldProps = {
    iimclient: ConfigClientType;
    api: ApiType;
    callbackNumber?: string;
    selectedQueueKey?: string;
    displayName?: string;
    changeWcbNumberErrorVisible: Function;
    changeWcbQueueErrorVisible?: Function;
    changeWcbErrorVisible: Function;
    changeWcbOkVisible: Function;
};

const CallCallbackStart = {
    lang_no: 'Bli oppringt',
    lang_se: 'Bli uppringd',
    lang_fi: 'Jätä soittopyyntö',
    lang_dk: 'Hvornår skal vi ringe?',
    lang_en: 'Call me back',
};

const CallCallbackStart2 = {
    lang_no: null,
    lang_se: 'Ring mig',
    lang_fi: null,
    lang_dk: 'Bestil opkald',
    lang_en: null,
};

const WcbClosedError = {
    lang_fi: null,
    lang_se: 'Uppringning är tyvärr inte tillgängligt',
    lang_dk: null,
    lang_no: 'Tilbakering er dessverre ikke tilgjengelig nå',
    lang_en: null,
};

const WcbButtonOld = ({
    iimclient,
    api,
    callbackNumber,
    selectedQueueKey,
    displayName,
    changeWcbNumberErrorVisible,
    changeWcbQueueErrorVisible,
    changeWcbErrorVisible,
    changeWcbOkVisible,
}: WcbButtonOldProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [wcbClosedErrorVisible, setWcbClosedErrorVisible] = useState(false);
    const { mypath, lang, callbackQueues } = iimclient;
    const myfullpath = iimclient.myfullpath || '';
    const buttonText =
        getTranslation(lang, CallCallbackStart2) || getTranslation(lang, CallCallbackStart);
    const phoneNumber = sanitizePhoneNumber(callbackNumber || '');
    const wcbClosedErrorText = getTranslation(lang, WcbClosedError);

    const orderCallbackSucceeded = (name, mobile) => {
        api.sendEventToCentalog(
            (name ? `name=${name}, ` : '') + `mobile=${mobile}`,
            'Create',
            'Order callback'
        );
    };

    const orderCallbackFailed = (name, mobile) => {
        api.sendEventToCentalog(
            (name ? `name=${name}, ` : '') + `mobile=${mobile}`,
            'Create',
            'Order callback',
            'Failure'
        );
    };

    const getPhoneCountryCode = () => {
        if (isNorwegian(iimclient)) return PhoneCountryCode.Norway;
        if (isDanish(iimclient)) return PhoneCountryCode.Denmark;
        if (isSwedish(iimclient)) return PhoneCountryCode.Sweden;
        // Finland altogether is handled in WcbButton.tsx component
    };

    const isQueueValid = (): boolean => {
        // Queue validation is only relevant for Sweden Private
        if (!iimclient.sepriv) return true;

        return (
            callbackQueues
                ?.map((queue) => queue.queueKey == selectedQueueKey)
                .reduce((a, b) => a || b) || false
        );
    };

    const getCallbackData = () => {
        let callbackData = {
            phone: phoneNumber,
            webshop: iimclient.webshop ? true : null,
            dkcom: false,
            dkpriv: false,
            secom: false,
            ksb: false,
        };

        if (isSwedish(iimclient)) {
            if (iimclient.sepriv) {
                Object.assign(callbackData, {
                    selectedQueueKey,
                    sepriv: true,
                    firstName: displayName,
                    lastName: '',
                });
            } else {
                callbackData.secom = true;
            }
        } else if (isDanish(iimclient)) {
            if (/\?dkcom/.test(myfullpath)) {
                callbackData.dkcom = true;
            } else if (/\?dkpriv/.test(myfullpath)) {
                callbackData.dkpriv = true;
            }
        } else if (/\?ksb/.test(myfullpath)) {
            callbackData.ksb = true;
        }

        return callbackData;
    };

    const clickWcbButton = () => {
        // Validation

        setWcbClosedErrorVisible(false);
        changeWcbNumberErrorVisible(false);
        changeWcbQueueErrorVisible?.(false);

        if (isOutsideOpeningHours(new Date())) {
            setWcbClosedErrorVisible(true);
            return;
        }

        let inputsAreValid = true;

        if (!isPhoneNumberValid(getPhoneCountryCode(), phoneNumber)) {
            changeWcbNumberErrorVisible(true);
            inputsAreValid = false;
        }
        if (!isQueueValid()) {
            changeWcbQueueErrorVisible?.(true);
            inputsAreValid = false;
        }

        if (!inputsAreValid) {
            return;
        }

        // Back-end request
        setIsLoading(true);
        axios
            .post(mypath + 'api/callback', getCallbackData())
            .then((res) => {
                const data = res.data;
                if (
                    data === 'OK' ||
                    data === 'OK2' ||
                    data.indexOf('puzzelapi.callout - OK -') > -1
                ) {
                    orderCallbackSucceeded(displayName, phoneNumber);
                    changeWcbOkVisible(true);
                } else {
                    orderCallbackFailed(displayName, phoneNumber);
                    changeWcbErrorVisible(true);
                }
            })
            .catch(() => {
                orderCallbackFailed(displayName, phoneNumber);
                changeWcbErrorVisible(true);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <button
                id="callbackBtn"
                type="button"
                className="if button primary u-margin-top--16 loader-btn"
                role="button"
                aria-label="Callback"
                disabled={isLoading}
                onClick={clickWcbButton}
            >
                <span aria-hidden="true" className="btn-text">
                    {buttonText}
                </span>
                {isLoading && (
                    <div aria-hidden="true" className="load-spinner">
                        <IconLoadSpinner width="32px" height="32px" color="#000" />
                    </div>
                )}
            </button>
            {wcbClosedErrorVisible && wcbClosedErrorText && (
                <div className="validation-error">
                    <span className="validationerror">{wcbClosedErrorText}</span>
                </div>
            )}
        </>
    );
};

export default WcbButtonOld;
